import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { API_BASE_URL } from "src/swagger/api.client.generated";

/** Matches BrainChanger's standard { resultStatus, responseData } API envelope. */
interface ResponseEnvelope<T> {
  resultStatus?: { statusCode?: number; errorText?: string };
  responseData?: T;
}

interface CreateCheckoutSessionResponse {
  url?: string;
}

interface VerifySessionResponse {
  verified?: boolean;
  subscriptionActive?: boolean;
}

interface SubscriptionStatusResponse {
  status?: string;
  isActive?: boolean;
}

interface CheckAccountSubscriptionResponse {
  accountExists?: boolean;
  hasActiveSubscription?: boolean;
}

/**
 * Hand-written rather than NSwag-generated: PaymentsController/StripeWebhookController
 * are new and this sandbox has no reachable SQL Server for BrainChanger.API to start
 * against (Startup.cs runs Database.Migrate() on boot and aborts if it can't reach a
 * database), so `npm run nswag` can't hit a live /swagger/v1/swagger.json here.
 * Regenerate api.client.generated.ts and switch to the generated `PaymentsService`
 * once run against a real backend - see changelog/2026-07-11-stripe-checkout-integration.md.
 */
@Injectable({ providedIn: "root" })
export class PaymentsService {
  // Cached so SubscriptionGuard doesn't round-trip on every navigation once we know
  // the account is active. Set directly by payment-success right after a successful
  // verify, and cleared on logout via clearCache().
  private cachedActive: boolean | null = null;

  // Reactive mirror of cachedActive so the app shell (showNav, the authenticated-init
  // fan-out) can gate on a *confirmed* active subscription rather than mere token
  // presence. Starts false: nothing authenticated runs until a check confirms it.
  private subscriptionActiveSource = new BehaviorSubject<boolean>(false);
  subscriptionActive$ = this.subscriptionActiveSource.asObservable();

  /** True only once a subscription check has confirmed an active subscription. */
  get isConfirmedActive(): boolean {
    return this.cachedActive === true;
  }

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  markSubscriptionActive() {
    this.cachedActive = true;
    this.subscriptionActiveSource.next(true);
  }

  clearCache() {
    this.cachedActive = null;
    this.subscriptionActiveSource.next(false);
  }

  async isSubscriptionActive(): Promise<boolean> {
    if (this.cachedActive) return true;
    const status = await this.subscriptionStatus();
    this.cachedActive = status.isActive;
    this.subscriptionActiveSource.next(!!status.isActive);
    return !!status.isActive;
  }

  async createCheckoutSession(planId: string): Promise<string> {
    const result = await this.http
      .post<ResponseEnvelope<CreateCheckoutSessionResponse>>(
        `${this.apiBaseUrl}/api/Payments/CreateCheckoutSession`,
        { planId }
      )
      .toPromise();

    if (result.resultStatus?.statusCode !== 1 || !result.responseData?.url) {
      throw new Error(
        result.resultStatus?.errorText || "Could not start checkout."
      );
    }

    return result.responseData.url;
  }

  async verifySession(sessionId: string): Promise<boolean> {
    const result = await this.http
      .get<ResponseEnvelope<VerifySessionResponse>>(
        `${this.apiBaseUrl}/api/Payments/VerifySession`,
        { params: { sessionId } }
      )
      .toPromise();

    return !!result.responseData?.verified;
  }

  async subscriptionStatus(): Promise<SubscriptionStatusResponse> {
    const result = await this.http
      .get<ResponseEnvelope<SubscriptionStatusResponse>>(
        `${this.apiBaseUrl}/api/Payments/SubscriptionStatus`
      )
      .toPromise();

    return result.responseData || { status: "none", isActive: false };
  }

  /**
   * Anonymous email lookup backing the login page's "already started an account"
   * prompt - see AccountController.CheckAccountSubscription. Hand-written for the
   * same reason as the rest of this file (see class comment); returns booleans
   * only, never a UserGuid or token.
   */
  async checkAccountSubscription(email: string): Promise<CheckAccountSubscriptionResponse> {
    const result = await this.http
      .post<ResponseEnvelope<CheckAccountSubscriptionResponse>>(
        `${this.apiBaseUrl}/api/Account/CheckAccountSubscription`,
        null,
        { params: { EmailAddress: email } }
      )
      .toPromise();

    return result.responseData || { accountExists: false, hasActiveSubscription: false };
  }
}
